import { api } from './api';
import type { District, DistrictCreateRequest, DistrictUpdateRequest, DeleteResponse, PaginatedResponse } from '../types';

export interface DistrictQueryParams {
  include_deleted?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: string;
  app_id?: string;   // Filter to one accessible project (intersected server-side)
  include_global?: boolean;   // Also include core/global rows (appId=null)
}

/** Re-scope preview: which projects are aligned to a district. */
export interface RescopePreview {
  canRescope: boolean;
  conflicts: Record<string, { mandals: number; units: number }>;
  references: Record<string, { mandals: number; units: number }>;
}

export const districtsService = {
  getAll: async (params?: DistrictQueryParams): Promise<PaginatedResponse<District>> => {
    const response = await api.get<PaginatedResponse<District>>('/api/v1/districts/list', { params });
    return response.data;
  },

  // Preview re-scoping a district to a project (platform admin only).
  getRescopePreview: async (id: string, appId?: string | null): Promise<RescopePreview> => {
    const response = await api.get<{ data: RescopePreview }>(
      `/api/v1/districts/${id}/rescope-preview`,
      { params: { app_id: appId || undefined } }
    );
    return response.data.data;
  },

  // Change a district's owning project (null/empty = global). Platform admin only.
  rescopeProject: async (id: string, appId: string | null): Promise<District> => {
    const response = await api.patch<{ data: District }>(
      `/api/v1/districts/${id}/project`,
      { appId: appId || null }
    );
    return response.data.data;
  },

  /**
   * Get all districts for dropdown (no pagination, fetches all records)
   */
  getAllForDropdown: async (): Promise<District[]> => {
    const response = await api.get<PaginatedResponse<District>>('/api/v1/districts/list', {
      params: {  include_deleted: false }
    });
    return response.data.data || [];
  },

  getById: async (id: string): Promise<District> => {
    const response = await api.get<{ data: District }>(`/api/v1/districts/get/${id}`);
    return response.data.data;
  },

  create: async (data: DistrictCreateRequest): Promise<District> => {
    const response = await api.post<District>('/api/v1/districts/create', data);
    return response.data;
  },

  update: async (id: string, data: DistrictUpdateRequest): Promise<District> => {
    const response = await api.put<District>(`/api/v1/districts/update/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<DeleteResponse> => {
    const response = await api.delete<DeleteResponse>(`/api/v1/districts/delete/${id}`);
    return response.data;
  },

  restore: async (id: string): Promise<District> => {
    const response = await api.patch<District>(`/api/v1/districts/restore/${id}`);
    return response.data;
  },
};
